import { DropdownButton, DropdownButtonProps } from '@dixa/lib-shared-habla';
import {
  ComponentProps,
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { aiApi } from 'apis/ai';
import { ConversationContext } from 'components/conversation';
import { EditorCopilotDropdownContext } from 'components/editorCopilotDropdown/editorCopilotDropdownContext';
import { useEditorContext } from 'components/editorCopilotDropdown/utils';
import { useCommandHandler } from 'utils/commands';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import { CopilotDropdownRoot } from './components';
import { copilotDropdownFeatureFlags } from './copilotDropdown.constants';
import {
  CopilotAction,
  CopilotActionComputed,
  CopilotActions,
  CopilotDropdownActionParams,
} from './copilotDropdown.types';
import {
  CopilotDropdownActionContext,
  CopilotDropdownActionContextValue,
} from './copilotDropdownActionContext';

/** Represents the props for the CopilotDropdown component. */
export type CopilotDropdownProps = {
  actions: CopilotActions;
} & Omit<DropdownButtonProps, 'ref' | 'dropdown'>;

export const useFindStyleGuideForChannel = (): boolean => {
  const { channel } = useContext(EditorCopilotDropdownContext);

  const { data: styleGuide } = aiApi.useReadAIWritingStyleGuideMatchQuery({
    features: 'impr_reply',
    ...(channel && { channels: channel }),
  });

  return !!styleGuide?.data?.match;
};

/** Represents a dropdown component providing AI-powered functionalities. */
export const CopilotDropdown = memo(function CopilotDropdown({
  actions,
  onToggle,
  ...dropdownButtonProps
}: CopilotDropdownProps): JSX.Element | null {
  const featureFlags = useFeatureFlag(copilotDropdownFeatureFlags);
  const [selectedAction, setSelectedAction] = useState<string>();
  const dropdownButtonRef = useRef<DropdownButton>(null);
  const conversationContext = useContext(ConversationContext);
  const dropdownContext = dropdownButtonRef.current?.contextState;
  const [dropdownUndoStack, setDropdownUndoStack] = useState<
    CopilotDropdownActionContextValue['dropdownUndoStack']
  >([]);
  const [currentEditorText, setCurrentEditorText] = useState<string>('');
  const { getSelectedText } = useEditorContext();

  const channelStyleGuideMatch = useFindStyleGuideForChannel();
  const actionParams: CopilotDropdownActionParams = useMemo(
    () => ({
      conversationContext,
      dropdownContext,
      featureFlags,
      channelStyleGuideMatch,
      currentEditorText,
    }),
    [
      channelStyleGuideMatch,
      conversationContext,
      currentEditorText,
      dropdownContext,
      featureFlags,
    ],
  );

  for (const actionName in actions) {
    const commandName = actions[actionName].commandName;
    if (commandName) {
      // Hooks shouldn't be used inside loops because loops can be dynamic.
      // However, in this use case, the list is fixed, so the loop will always
      // be the same in runtime. Accordingly, using hooks here is safe.
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useCommandHandler({
        name: commandName,
        actionFn: () => {
          dropdownButtonRef.current?.open();
          handleSelectionChange(actionName);
        },
      });
    }
  }

  const handleDropdownToggle = useCallback(
    (isOpen: boolean, isFirstOpen?: boolean) => {
      if (!isOpen) {
        setDropdownUndoStack([]);
        setSelectedAction(undefined);
      }

      setCurrentEditorText(getSelectedText());

      onToggle?.(isOpen, isFirstOpen);
    },
    [getSelectedText, onToggle],
  );

  const handleSelectionChange = useCallback(
    (selection: string | undefined) => {
      const action: CopilotAction | undefined = selection
        ? actions[selection]
        : undefined;

      if (action && 'action' in action) {
        action.action(actionParams);
      } else {
        setSelectedAction(selection);
      }
    },
    [actionParams, actions],
  );

  const copilotDropdownActionContextValue =
    useMemo<CopilotDropdownActionContextValue>(() => {
      const actionsComputed = Object.keys(actions).reduce<
        Record<keyof CopilotActions, CopilotActionComputed>
      >((prevValue, actionKey) => {
        const action = actions[actionKey];
        return {
          ...prevValue,
          [actionKey]: {
            ...action,
            title:
              typeof action?.title === 'string'
                ? action.title
                : action?.title(actionParams),
            isDisabled: action?.isDisabled?.(actionParams),
            isVisible: action?.isVisible?.(actionParams),
          },
        } as Record<keyof CopilotActions, CopilotActionComputed>;
      }, {} as Record<keyof CopilotActions, CopilotActionComputed>);

      return {
        featureFlags,
        actions,
        actionsComputed,
        dropdownUndoStack,
        latestUndoStackItem: dropdownUndoStack[dropdownUndoStack.length - 1],
        resetDropdownUndoStack: () => setDropdownUndoStack([]),
        pushDropdownUndoStack: (content, feedbackId, additionalFeedback) => {
          setDropdownUndoStack([
            ...dropdownUndoStack,
            {
              content,
              feedbackId: feedbackId ?? null,
              additionalFeedback: additionalFeedback || {},
            },
          ]);
        },
        popDropdownUndoStack: () => {
          const lastItem = dropdownUndoStack[dropdownUndoStack.length - 1];
          const content = dropdownUndoStack.slice(0, -1);
          setDropdownUndoStack(content);
          return lastItem;
        },
        goToAction: (action, draftContent, undoStackAction = 'push') => {
          const undoItem = {
            content: draftContent,
            feedbackId: null,
            additionalFeedback: {},
          };
          const newEditorDraftContent =
            undoStackAction === 'push'
              ? [...dropdownUndoStack, undoItem]
              : [undoItem];
          setDropdownUndoStack(newEditorDraftContent);
          return handleSelectionChange(action);
        },
        onBack: () => handleSelectionChange(undefined),
      };
    }, [
      actionParams,
      actions,
      dropdownUndoStack,
      featureFlags,
      handleSelectionChange,
    ]);

  /**
   * The content of the dropdown per selected action. If the action definition
   * doesn't contain `action` attribute, then it should render a component to be
   * rendered.
   */
  const dropdownContent = useMemo(() => {
    const action: CopilotAction | undefined = selectedAction
      ? actions[selectedAction]
      : undefined;

    const content =
      action && 'content' in action ? (
        action.content
      ) : (
        <CopilotDropdownRoot onSelectionChange={handleSelectionChange} />
      );

    return content;
  }, [actions, handleSelectionChange, selectedAction]);

  const dropdownProps = useMemo<
    ComponentProps<typeof DropdownButton>['dropdownProps']
  >(() => {
    return {
      // Since the dropdown can trigger a feedback modal that renders outside
      // of the dropdown, we need to prevent the dropdown from closing when
      // interacting with it.
      closeOnBlur: false,
    };
  }, []);

  return (
    <CopilotDropdownActionContext.Provider
      value={copilotDropdownActionContextValue}
    >
      <DropdownButton
        {...dropdownButtonProps}
        ref={dropdownButtonRef}
        onToggle={handleDropdownToggle}
        dropdown={dropdownContent}
        dropdownProps={dropdownProps}
      />
    </CopilotDropdownActionContext.Provider>
  );
});
